import React from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';

import { FileInput } from '~/components/file';
import { CaraWorkspaceFileInput } from '~/api/graphql';
import { renameUploadedFiles } from './presets.utils';

/**
 * Repeatable upload buckets, each under one name the user supplies.
 *
 * A preset that compares several quoted options needs the join between an
 * option's rate file and its benefit summary to come from the person who
 * received them. Inferring it downstream is the one pairing error that survives
 * every arithmetic check, because the math on both halves is right and only the
 * join is broken.
 *
 * Which document is which *inside* one option is a different question, and the
 * prompt answers it from a document glossary: a rate exhibit does not read like
 * an SBC. So a card asks for the boundary the documents cannot supply — whose
 * carrier this is — and takes everything belonging to that carrier in one
 * bucket rather than asking the broker to sort their own packet by role.
 */

export type PresetOptionCard = {
  /**
   * Identity across renders. `FileInput` holds its own selection, so a card must
   * keep its React key when a card above it is removed or its uploads follow the
   * wrong carrier.
   */
  id: string;
  name: string;
  files: CaraWorkspaceFileInput[];
};

/** A card carrying at least one document, numbered by its place among such cards. */
export type NumberedPresetOption = {
  ordinal: number;
  name: string;
  card: PresetOptionCard;
};

let cardsCreated = 0;

const emptyCard = (): PresetOptionCard => {
  cardsCreated += 1;
  return { id: `option-${cardsCreated}`, name: '', files: [] };
};

/**
 * The cards worth sending, renumbered so the ordinal in the message contract is
 * the ordinal in the upload prefixes. A card with no documents is not an option,
 * and leaving a gap in the sequence for one would associate a name in the
 * contract with a set of files that carries a different number.
 */
export const numberedPresetOptions = (cards: PresetOptionCard[]): NumberedPresetOption[] =>
  cards
    .filter((card) => card.files.length > 0)
    .map((card, index) => ({ ordinal: index + 1, name: card.name.trim(), card }));

/**
 * The `<label> N: <name>` lines naming each option. An option whose name was
 * left blank is still priced from its documents, so it contributes no line
 * rather than a placeholder the report would print as a carrier.
 */
export const presetOptionNameLines = (options: NumberedPresetOption[], label: string): string[] =>
  options
    .filter((option) => option.name.length > 0)
    .map((option) => `${label} ${option.ordinal}: ${option.name}`);

export const presetOptionUploads = (options: NumberedPresetOption[]): CaraWorkspaceFileInput[] =>
  options.flatMap(({ ordinal, card }) => renameUploadedFiles(card.files, `[Option ${ordinal}] `));

/** Every staged upload, including cards that will be dropped, for the progress bar. */
export const presetOptionFiles = (cards: PresetOptionCard[]): CaraWorkspaceFileInput[] =>
  cards.flatMap((card) => card.files);

export const usePresetOptionCards = (initialCards = 2) => {
  const [cards, setCards] = React.useState<PresetOptionCard[]>(() =>
    Array.from({ length: initialCards }, emptyCard)
  );

  const update = (id: string, change: (card: PresetOptionCard) => PresetOptionCard) =>
    setCards((prev) => prev.map((card) => (card.id === id ? change(card) : card)));

  return {
    cards,
    add: () => setCards((prev) => [...prev, emptyCard()]),
    // One card always remains: with none there is no visible affordance left to
    // add the first option back.
    remove: (id: string) =>
      setCards((prev) => (prev.length > 1 ? prev.filter((card) => card.id !== id) : prev)),
    rename: (id: string, name: string) => update(id, (card) => ({ ...card, name })),
    setFiles: (id: string, files: CaraWorkspaceFileInput[]) =>
      update(id, (card) => ({ ...card, files })),
    options: numberedPresetOptions(cards),
    files: presetOptionFiles(cards)
  };
};

export const PresetOptionCards = (props: {
  cards: PresetOptionCard[];
  /** Singular noun for one card, as in "Option 1" and "Add another option". */
  noun: string;
  namePlaceholder: string;
  /**
   * Short, because it repeats once per card. What belongs in the bucket is
   * described once above the whole group instead.
   */
  uploadLabel: string;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onFilesChange: (id: string, files: CaraWorkspaceFileInput[]) => void;
}) => (
  <div className="flex flex-col gap-3">
    {props.cards.map((card, index) => (
      <div
        key={card.id}
        className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-3 flex flex-col gap-3"
      >
        <div className="flex flex-row items-center justify-between gap-2">
          <span className="text-sm font-medium text-neutral-900 dark:text-white">
            {props.noun} {index + 1}
          </span>
          {props.cards.length > 1 && (
            <button
              type="button"
              aria-label={`Remove ${props.noun.toLowerCase()} ${index + 1}`}
              onClick={() => props.onRemove(card.id)}
              className="text-neutral-500 dark:text-neutral-400 rounded-full p-0.5 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white transition-all duration-150"
            >
              <XMarkIcon className="size-4" />
            </button>
          )}
        </div>
        <Input
          aria-label={`${props.noun} ${index + 1} name`}
          value={card.name}
          placeholder={props.namePlaceholder}
          onChange={(e) => props.onRename(card.id, e.currentTarget.value)}
        />
        <Field>
          <Label>{props.uploadLabel}</Label>
          <FileInput
            multiple
            enableExisting
            onChange={(change) => props.onFilesChange(card.id, change.files)}
          />
        </Field>
      </div>
    ))}
    <div>
      <button
        type="button"
        onClick={props.onAdd}
        className="group text-neutral-600 dark:text-neutral-400 rounded-full border border-neutral-200 dark:border-neutral-700 flex flex-row items-center gap-1 text-xs py-1 px-1.5 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white transition-all duration-150"
      >
        <PlusIcon className="size-3 group-hover:text-neutral-900 dark:group-hover:text-white" />
        Add another {props.noun.toLowerCase()}
      </button>
    </div>
  </div>
);
